import React, { useState } from 'react'
import { Switch, Link, useLocation } from 'react-router-dom'

import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import SecurityIcon from '@mui/icons-material/Security'
import RecentActorsIcon from '@mui/icons-material/RecentActors'
import MemoryIcon from '@mui/icons-material/Memory'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import OAuthIcon from './components/OAuthIcon'

import NavBar from './components/NavBar'
import Users from './pages/User/Users'
import { View } from './types'
import { ProtectedRoute } from '@dentalux/security/lib'
import UserChanges from './pages/UserChanges/UserChanges'
import SystemUsers from './pages/SystemUser/SystemUser'
import { Box, Drawer, Tab, Tabs, Tooltip, Typography, Zoom } from '@mui/material'
import Tenants from './pages/Tenant/Tenants'
import OAuth from './pages/OAuth/OAuth'
import RolesList from './pages/Roles'
import RoleDetails from './pages/Roles/RoleDetails'
import AuthLog from './pages/AuthLog/AuthLog'

interface MenuTooltipProps {
  text: string
  children: React.ReactElement<any, any>
}

const MenuTooltip: React.FC<MenuTooltipProps> = ({ text, children }) => {
  return (
    <Tooltip
      title={text}
      placement="right"
      TransitionComponent={Zoom}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'common.white',
            color: 'text.primary',
            boxShadow: 1,
            fontSize: 'fontSize',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  )
}

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-force-tabpanel-${index}`}
      aria-labelledby={`scrollable-force-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={1}>{children}</Box>}
    </Typography>
  )
}

const menuItems = ['/', '/changes', '/system-users', '/tenants', '/oauth', '/roles', '/applications', '/auth-logs']

const Main = () => {
  const location = useLocation()
  const [open, setOpen] = useState(true)
  const [navItems] = useState<React.ReactNode>(null)

  const handleMenuClose = () => {
    setOpen(!open)
  }

  return (
    <Box>
      <NavBar handleMenuClose={handleMenuClose} navItems={navItems} />
      <Drawer
        variant="persistent"
        sx={{
          width: 80,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: 80, boxSizing: 'border-box', top: 'inherit' },
        }}
        elevation={2}
        open={open}
      >
        <Tabs
          orientation="vertical"
          variant="scrollable"
          scrollButtons={false}
          aria-label="side navigation"
          value={menuItems.includes(location.pathname) ? location.pathname : false}
          textColor="primary"
        >
          <Tab
            value={'/'}
            label=""
            icon={
              <MenuTooltip text="Users">
                <AssignmentIndIcon />
              </MenuTooltip>
            }
            to="/"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/changes'}
            label=""
            icon={
              <MenuTooltip text="User changes">
                <RecentActorsIcon />
              </MenuTooltip>
            }
            to="/changes"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/system-users'}
            label=""
            icon={
              <MenuTooltip text="System users">
                <MemoryIcon />
              </MenuTooltip>
            }
            to="/system-users"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/tenants'}
            label=""
            icon={
              <MenuTooltip text="Tenants">
                <AccountBalanceIcon />
              </MenuTooltip>
            }
            to="/tenants"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/oauth'}
            label=""
            icon={
              <MenuTooltip text="OAuth clients">
                <OAuthIcon />
              </MenuTooltip>
            }
            to="/oauth"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/roles'}
            label=""
            icon={
              <MenuTooltip text="Roles and permissions">
                <SecurityIcon />
              </MenuTooltip>
            }
            to="/roles"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
          <Tab
            value={'/auth-logs'}
            label=""
            icon={
              <MenuTooltip text="Authentication log">
                <VpnKeyIcon />
              </MenuTooltip>
            }
            to="/auth-logs"
            component={Link}
            sx={{ minWidth: '80px' }}
          />
        </Tabs>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: (theme) => `${theme.spacing(1)} ${theme.spacing(2)}`,
          ml: open ? 10 : 0,
        }}
      >
        <TabPanel value={location.pathname} index={location.pathname}>
          <Switch>
            <ProtectedRoute path="/" exact view={View.USER_MANAGEMENT}>
              <Users />
            </ProtectedRoute>

            <ProtectedRoute path="/changes" exact view={View.USER_MANAGEMENT}>
              <UserChanges />
            </ProtectedRoute>

            <ProtectedRoute path="/system-users" exact view={View.USER_MANAGEMENT}>
              <SystemUsers />
            </ProtectedRoute>

            <ProtectedRoute path="/tenants" exact view={View.USER_MANAGEMENT}>
              <Tenants />
            </ProtectedRoute>

            <ProtectedRoute path="/oauth" exact view={View.ROLE_MANAGEMENT}>
              <OAuth />
            </ProtectedRoute>

            <ProtectedRoute path="/roles" exact view={View.ROLE_MANAGEMENT}>
              <RolesList />
            </ProtectedRoute>
            <ProtectedRoute path="/roles/:roleId" exact view={View.ROLE_MANAGEMENT}>
              <RoleDetails />
            </ProtectedRoute>

            <ProtectedRoute path="/auth-logs" exact>
              <AuthLog />
            </ProtectedRoute>
          </Switch>
        </TabPanel>
      </Box>
    </Box>
  )
}

export default Main
